import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { VideoStateTool } from './VideoStateTool';

import { checkpointVideo } from 'js/src/data/video-data';
import { intToHex, parseHexColor } from 'js/src/libs/color-utils';
import { saveRawLuminary } from 'js/src/luminary';

export const legacySetBackgroundSchema = z.object({
    scene_index: z.number().describe('The scene index (0, 1, 2, etc.)'),
    asset_name: z.string().describe('The asset name (e.g. color_1)'),
    color: z
        .string()
        .regex(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/)
        .describe('The color as a hex code.'),
});
export type SetBackgroundArgs = z.infer<typeof legacySetBackgroundSchema>;

export class LegacySetBackgroundTool extends VideoStateTool<typeof legacySetBackgroundSchema> {
    protected override readonly schema = legacySetBackgroundSchema;
    protected override readonly toolName = TOOL_NAMES.setBackground;
    public override readonly subagentAccess = SubagentAccess.UNAVAILABLE;
    protected override readonly toolDescription =
        'Set background color for a specific scene and asset';

    public override getToolDisplayName(): string {
        return 'Setting background';
    }

    public get statusIcon(): string {
        return 'wallpaper';
    }

    public async execute(args: SetBackgroundArgs, context: ToolExecutionContext) {
        const { scene_index, asset_name, color } = args;
        const { parentActionGroup } = context;

        const colorInt = parseHexColor(color);
        if (colorInt === null) {
            return 'Error: color must be a hex string like "#ff0000"';
        }

        try {
            if (scene_index >= this.luminary.scenes.length) {
                return `Error: scene_index ${scene_index} is out of bounds for the current video`;
            }
            const scene = this.luminary.scenes[scene_index];
            if (!scene) {
                return `Error: No scene found at index ${scene_index}`;
            }

            const asset = scene.getAssetByName(asset_name);
            if (!asset) {
                return `Error: No asset found with name ${asset_name} in scene ${scene_index}`;
            }

            // Check if asset is a ColorAsset
            if (!VideoData.isColorAsset(asset)) {
                return `Error: Asset ${asset_name} in scene ${scene_index} is not a color asset`;
            }

            try {
                asset.sourceColor = colorInt;

                // Save changes to enable undo/redo functionality
                this.dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(this.luminary),
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
            } catch (error) {
                return `Error: Failed to set color for asset ${asset_name} in scene ${scene_index}`;
            }
            return `Success: Set background color for scene ${scene_index}, asset ${asset_name} to ${intToHex(colorInt)}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting background color: ${errorMessage}`;
        }
    }
}
